import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';

// ============================================================================
// Widget assessments panel (adviser side).
// ============================================================================
// Shows what the lead — or their employer — actually completed in the widget:
// CRS / Express Entry, the OINP Ontario Workforce Priority check, and the OINP
// employer eligibility check.
//
// Two things this panel is deliberate about:
//
//   1. A missing score is rendered as "not published", never as 0. The OINP
//      engine returns a null total whenever any factor's official points table
//      is still unverified, and an adviser must be able to tell "we could not
//      score this" apart from "they scored badly".
//   2. If the browser's number disagreed with the server's recomputation, the
//      divergence is shown rather than hidden — that means the widget bundle is
//      running a different rule version than the server.
//
// Renders nothing when the case has no assessments, so it is safe to mount
// unconditionally.
// ============================================================================

const Section = styled.div`
  margin-bottom: 20px;
  background: rgba(30, 35, 50, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 12px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.12);

  svg { width: 18px; height: 18px; color: #7fb3e8; flex-shrink: 0; }
  h4 { margin: 0; font-size: 14px; font-weight: 600; color: #e8eaed; }
`;

const Body = styled.div`
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CardRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 10px;
  background: rgba(15, 20, 30, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.12);
`;

const CardMain = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #e8eaed;
  margin-bottom: 5px;
`;

const CardMeta = styled.div`
  font-size: 11.5px;
  line-height: 1.55;
  color: #9aa5b4;
`;

const ScoreBlock = styled.div`
  flex-shrink: 0;
  text-align: right;
  min-width: 84px;
`;

const ScoreValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: ${p => p.$color || '#e8eaed'};
`;

const ScoreMax = styled.div`
  font-size: 10.5px;
  color: #7a8494;
  margin-top: 2px;
`;

const NoScore = styled.div`
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  color: #d9a13b;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${p => p.$color};
  background: ${p => p.$bg};
`;

const Warning = styled.div`
  padding: 11px 14px;
  border-radius: 9px;
  font-size: 11.5px;
  line-height: 1.55;
  color: #f0c674;
  background: rgba(217, 161, 59, 0.1);
  border: 1px solid rgba(217, 161, 59, 0.28);
`;

const FactorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 5px 16px;
  margin-top: 11px;
  padding-top: 11px;
  border-top: 1px solid rgba(100, 150, 200, 0.1);
`;

const Factor = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: #9aa5b4;

  strong {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${p => (p.$known ? '#cdd5df' : '#6c7686')};
  }
`;

const TYPE_LABELS = {
  crs: 'Express Entry — CRS',
  oinp_candidate: 'Ontario PNP — Workforce Priority (EOI)',
  oinp_employer: 'OINP employer eligibility',
};

const FACTOR_LABELS = {
  teer: 'TEER category',
  occupationalCategory: 'Occupational category',
  wage: 'Hourly wage',
  ontarioExperience: 'Ontario work experience',
  canadianEarnings: 'Canadian earnings',
  legalStatus: 'Legal status',
  education: 'Highest education',
  canadianCredentials: 'Canadian credentials',
  lowestClb: 'Lowest CLB',
  officialLanguages: 'Official languages',
  region: 'Job offer region',
};

const VERDICT_STYLE = {
  'appears-eligible': { label: 'Appears eligible', color: '#7ee2a8', bg: 'rgba(126, 226, 168, 0.13)' },
  'needs-verification': { label: 'Needs verification', color: '#f0c674', bg: 'rgba(240, 198, 116, 0.13)' },
  'not-eligible': { label: 'Not eligible', color: '#f28b82', bg: 'rgba(242, 139, 130, 0.13)' },
  'needs-job-offer': { label: 'Needs a job offer', color: '#f0c674', bg: 'rgba(240, 198, 116, 0.13)' },
};

const scoreColor = (score, max) => {
  if (score == null || !max) return '#e8eaed';
  const ratio = score / max;
  if (ratio >= 0.7) return '#7ee2a8';
  if (ratio >= 0.5) return '#f0c674';
  return '#f28b82';
};

const formatDate = value => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const WidgetAssessments = ({ threadId }) => {
  const axios = useAuthenticatedAxios();
  const [assessments, setAssessments] = useState([]);
  const [hasDivergence, setHasDivergence] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!threadId) return;
    try {
      const response = await axios.get(`/assessments/${threadId}`);
      setAssessments(response.data?.assessments || []);
      setHasDivergence(Boolean(response.data?.hasDivergence));
    } catch (error) {
      // A case with no assessments is the common path, not an error state.
      setAssessments([]);
    } finally {
      setLoaded(true);
    }
  }, [axios, threadId]);

  useEffect(() => { load(); }, [load]);

  // A client can complete an assessment while the adviser has this case open, and
  // there is no socket channel between widget-server and this dashboard (platform
  // notifications still carry a TODO for that). Refetching when the tab regains
  // focus is the cheap correct fix: no new infrastructure, and a stale panel can
  // only survive as long as the adviser never looks away.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  if (!loaded || assessments.length === 0) return null;

  return (
    <Section>
      <Header>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
        </svg>
        <h4>Widget assessments</h4>
      </Header>

      <Body>
        {hasDivergence && (
          <Warning>
            One of these assessments was scored differently by the widget and the server. The value
            shown is the server&apos;s recomputation from the raw answers. This usually means the
            deployed widget bundle is running a different rule version — worth checking before
            quoting a score to the client.
          </Warning>
        )}

        {assessments.map(assessment => {
          const verdict = VERDICT_STYLE[assessment.verdict];
          const result = assessment.result || {};
          const breakdown = Array.isArray(result.breakdown) ? result.breakdown : [];

          return (
            <div key={assessment._id}>
              <CardRow>
                <CardMain>
                  <CardTitle>{TYPE_LABELS[assessment.type] || assessment.type}</CardTitle>
                  <CardMeta>
                    {verdict && <Pill $color={verdict.color} $bg={verdict.bg}>{verdict.label}</Pill>}
                    {assessment.createdAt && <> &nbsp;{formatDate(assessment.createdAt)}</>}
                    {assessment.rulesVersion && <> · rules {assessment.rulesVersion}</>}
                    {assessment.source && assessment.source !== 'widget' && <> · via {assessment.source}</>}
                    {assessment.capturedEmail && <><br />Contact captured: {assessment.capturedEmail}</>}
                    {result.tier && (
                      <><br />Work location tier: {result.tier.id} — revenue floor $
                        {Number(result.tier.revenueFloor).toLocaleString()}, min {result.tier.minEmployees} FT CC/PR staff</>
                    )}
                    {result.noc && (
                      <><br />NOC {result.noc.code} · TEER {result.noc.teer}</>
                    )}
                    {Array.isArray(result.failureIds) && result.failureIds.length > 0 && (
                      <><br />Fails: {result.failureIds.join(', ')}</>
                    )}
                  </CardMeta>
                </CardMain>

                <ScoreBlock>
                  {assessment.score != null ? (
                    <>
                      {/* A score and an eligibility verdict are independent — a
                          client can score well and still fail a gate. Showing the
                          number in success colours next to "not eligible" reads
                          as a pass, so a blocked score is greyed and labelled. */}
                      <ScoreValue
                        $color={assessment.verdict === 'not-eligible'
                          ? '#7a8494'
                          : scoreColor(assessment.score, assessment.maxScore)}
                      >
                        {assessment.score}
                      </ScoreValue>
                      <ScoreMax>of {assessment.maxScore}</ScoreMax>
                      {assessment.verdict === 'not-eligible' && (
                        <NoScore style={{ marginTop: 4 }}>not achievable<br />on these answers</NoScore>
                      )}
                    </>
                  ) : (
                    <NoScore>
                      No score
                      <br />published
                    </NoScore>
                  )}
                </ScoreBlock>
              </CardRow>

              {assessment.scoreState === 'incomplete-ruleset' && (
                <CardMeta style={{ padding: '8px 16px 0' }}>
                  Score withheld — no verified points table for:{' '}
                  {(assessment.missingFactors || []).map(id => FACTOR_LABELS[id] || id).join(', ')}.
                </CardMeta>
              )}

              {breakdown.length > 0 && (
                <FactorGrid>
                  {breakdown.map(factor => (
                    <Factor key={factor.id} $known={factor.known}>
                      <span>{FACTOR_LABELS[factor.id] || factor.id}</span>
                      <strong>
                        {factor.known
                          ? `${factor.points}/${factor.max}`
                          // "n/a" and "—" mean different things: hourly wage does
                          // not apply to self-employed physicians at all, whereas
                          // "—" means the factor could not be scored.
                          : factor.notApplicable ? 'n/a' : `—/${factor.max}`}
                      </strong>
                    </Factor>
                  ))}
                </FactorGrid>
              )}
            </div>
          );
        })}
      </Body>
    </Section>
  );
};

export default WidgetAssessments;
